import React, { useState } from 'react'
import { useSurveyQuestions } from '../../../hooks/use-survey-questions'
import { NavigationButtons } from '../shared/navigation-buttons'
import { SurveyTipsModal } from '../shared/survey-tips-modal'
import { SpectrumQuestion } from './question-types/spectrum-question'
import { ScaleQuestion } from './question-types/scale-question'
import { RoleSelector } from './role-selector'
import { Loader2 } from 'lucide-react'
import { SurveyHeader } from '../shared/survey-header'
import { Card, CardContent } from '../../ui/card'

const SECTIONS = [
  'Role Selection',
  'Approach to Leadership',
  'Current Focus',
  'Aspirational Focus',
  'Final Questions'
]

interface SurveyQuestionsProps {
  surveyCode: string
  onBackToInstructions: () => void
}

export function SurveyQuestions({ 
  surveyCode, 
  onBackToInstructions
}: SurveyQuestionsProps) {
  const [showTips, setShowTips] = useState(false)
  const [autoAdvance, setAutoAdvance] = useState(true)
  const [selectedRole, setSelectedRole] = useState<{ roleId: string | null; customRole: string | null }>({
    roleId: null,
    customRole: null
  })
  const {
    isLoading,
    currentQuestion,
    currentQuestionIndex,
    responses,
    currentSection,
    saveRole,
    handleResponse,
    handlePrevious,
    handleNext,
    progress,
    questions,
    questionsInSection,
    isLastInSection,
    isLastQuestion,
    hasError
  } = useSurveyQuestions(surveyCode)

  const hasExistingResponses = Object.keys(responses).length > 0

  if (isLoading) {
    return (
      <div className="container py-24">
        <div className="flex items-center justify-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-lg text-muted-foreground">Loading questions...</p>
        </div>
      </div>
    )
  }

  if (hasError) {
    return (
      <div className="container py-24">
        <div className="text-center space-y-4">
          <p className="text-lg text-muted-foreground">
            Unable to load survey questions. Please try again later.
          </p>
        </div>
      </div>
    )
  }

  if (!currentQuestion && currentSection !== 0) {
    return (
      <div className="container py-24">
        <div className="text-center space-y-4">
          <p className="text-lg text-muted-foreground">
            No questions available for this section.
          </p>
        </div>
      </div>
    )
  }

  const hasAnswered = currentQuestion ? responses[currentQuestion.id] !== undefined : false
  const isFirstInSection = questionsInSection.findIndex(q => q.id === currentQuestion?.id) === 0
  const questionNumber = questionsInSection.findIndex(q => q.id === currentQuestion?.id) + 1
  const showAutoAdvanceOption = currentSection > 0 && currentSection < 4 // Show auto-advance in sections 2-4

  const handleAnswerChange = (value: number) => {
    handleResponse(value)
    if (showAutoAdvanceOption && autoAdvance) {
      setTimeout(() => {
        if (isLastInSection) {
          // If it's the last question in the section, move to next section
          handleNext()
        } else if (!isLastQuestion) {
          // If it's not the last question overall, move to next question
          handleNext()
        }
      }, 300)
    }
  }

  const handleRoleSelect = (roleId: string | null, customRole: string | null) => {
    setSelectedRole({ roleId, customRole })
  }

  const handleRoleComplete = () => {
    handleNext()
  }

  return (
    <div className="container py-8">
      <div className="max-w-3xl mx-auto space-y-8">
        <SurveyHeader
          currentSection={currentSection}
          sectionName={SECTIONS[currentSection]}
          progress={progress}
          onShowTips={() => setShowTips(true)}
          onBackToInstructions={onBackToInstructions}
          questionNumber={currentQuestion ? questionNumber : undefined}
          totalQuestionsInSection={currentQuestion ? questionsInSection.length : undefined}
        />

        {currentSection === 0 ? (
          <RoleSelector
            savedRole={selectedRole}
            onRoleSelect={handleRoleSelect}
            onComplete={handleRoleComplete}
          />
        ) : currentQuestion ? (
          <Card>
            <CardContent className="p-8">
              <div className="flex flex-col h-[400px]">
                {/* Question text container with fixed height */}
                <div className="h-[140px] mb-8 overflow-y-auto">
                  <h3 className="text-base sm:text-lg md:text-2xl font-medium">
                    {currentQuestion.question_text}
                  </h3>
                </div>

                {/* Response options container */}
                <div className="flex-1 flex flex-col items-center justify-center">
                  {currentQuestion.question_type === 'current_spectrum' || 
                   currentQuestion.question_type === 'aspirational_spectrum' ? (
                    <SpectrumQuestion
                      question={currentQuestion}
                      value={responses[currentQuestion.id]}
                      onChange={handleAnswerChange}
                    />
                  ) : (
                    <ScaleQuestion
                      question={currentQuestion}
                      value={responses[currentQuestion.id]}
                      onChange={handleAnswerChange}
                    />
                  )}
                </div>

                {/* Navigation and auto-advance controls */}
                <div className="mt-auto pt-8">
                  <NavigationButtons
                    canGoBack={currentQuestionIndex > 0 || currentSection > 0}
                    hasAnswered={hasAnswered}
                    isLastQuestion={isLastQuestion}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    showPreviousSection={isFirstInSection && currentSection > 1}
                    showNextSection={isLastInSection || isLastQuestion}
                    hasExistingResponses={hasExistingResponses}
                  />
                  
                  {showAutoAdvanceOption && (
                    <div className="flex justify-center mt-4">
                      <label className="text-sm text-muted-foreground flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={autoAdvance}
                          onChange={(e) => setAutoAdvance(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        Automatically advance
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      {showTips && <SurveyTipsModal onClose={() => setShowTips(false)} />}
    </div>
  )
}